import { useCallback, useEffect, useState } from 'react'
import { packetThreadPoolApi } from 'utils/packet-workers'
import {
  SlntHeader,
  extractIPacketsFromSlnt,
  extractVariableTelemSamplesFromSlnt,
  extractMultiMotorTelemSamplesFromSlnt,
  getSlntHeader,
  slntParse,
  trySlntFormatV2
} from 'utils/slnt-file'

type PlaybackSessionOptions = {
  file: File
}

type PlaybackSession = {
  isPlayback: boolean
  name: string
  header?: SlntHeader
}

let playbackStreamInterval: NodeJS.Timeout | undefined

const stopPlaybackStream = () => {
  clearInterval(playbackStreamInterval)
  playbackStreamInterval = undefined
}

let _playbackSession: PlaybackSession | undefined
const _playbackSessionListeners: (() => void)[] = []

const _notify = () => _playbackSessionListeners.forEach((l) => l())

const _endSession = async () => {
  if (_playbackSession !== undefined) {
    stopPlaybackStream()
  }
  _playbackSession = undefined
}

const _startSession = async (
  playbackSessionOptions: PlaybackSessionOptions
): Promise<PlaybackSession | undefined> => {
  const fileContents = await playbackSessionOptions.file.text()

  const slntFile = slntParse(fileContents)

  const packets = extractIPacketsFromSlnt(slntFile)
  const vtss = extractVariableTelemSamplesFromSlnt(slntFile)
  let header
  let mmtss
  const slntV2 = trySlntFormatV2(slntFile)
  if (slntV2) {
    header = getSlntHeader(slntV2)
    mmtss = extractMultiMotorTelemSamplesFromSlnt(slntV2)
  }

  await packetThreadPoolApi.clear()
  if (header) {
    await packetThreadPoolApi.loadTelemMap(header.telemSet)
    await packetThreadPoolApi.loadMultiMotorTelemSet(header.multiMotorTelemSet ?? [])
    await packetThreadPoolApi.setActuatorDispositions(header.subActuators ?? {})
    await packetThreadPoolApi.loadTelemHeader(header)
  }
  await packetThreadPoolApi.handlePackets(packets)
  await packetThreadPoolApi.handleVarTelemPlayback(vtss)
  if (mmtss) await packetThreadPoolApi.handleMultiMotorTelemPlayback(mmtss)

  return {
    isPlayback: true,
    name: playbackSessionOptions.file.name,
    header
  }
}

export const usePlaybackSession = () => {
  const [playbackSession, setPlaybackSession] = useState<PlaybackSession | undefined>(
    _playbackSession
  )

  useEffect(() => {
    const onPlaybackSessionChange = () => setPlaybackSession(_playbackSession)
    _playbackSessionListeners.push(onPlaybackSessionChange)

    return () => {
      const index = _playbackSessionListeners.indexOf(onPlaybackSessionChange)
      if (index === -1) {
        return
      }

      _playbackSessionListeners.splice(index, 1)
    }
  }, [])

  const startSession = useCallback(async (options: PlaybackSessionOptions) => {
    _playbackSession = await _startSession(options)
    _notify()
    return _playbackSession
  }, [])

  const endSession = useCallback(async () => {
    await _endSession()
    _notify()
  }, [])

  return {
    playbackSession,
    startSession,
    endSession
  }
}
